import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { traAdmin, type TraCertificationEdition } from '@/lib/api/traAdminClient';
import ModuleLayout from '../../components/admin/ModuleLayout';
import { inputCls, labelCls, btnSave, btnSecondary, StyledSelect } from '../../components/admin/FormComponents';
import { traNavTabs } from '@/components/tra/traNavTabs';
import { toastError, toastSuccess } from '../../lib/toast';
import { getErrorMessage } from '../../lib/errors';
import { LoadState } from "@/components/ui/LoadState"

// tra-member-manual-certification: reconfigurar edición, fecha de
// certificación y número de certificado de un miembro ya existente en la
// lista de certificados — sobre todo el grupo dado de alta por el backfill
// pre-gestor, que nunca tuvo estos datos, pero sirve para corregir
// cualquier registro. Página completa, no modal (mismo criterio que
// AddWaitlistEntryPage.tsx).
export default function MemberDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: member, isLoading } = useQuery({
    queryKey: ['tra-member', id],
    queryFn: () => traAdmin.getMember(id!),
    enabled: !!id,
  });

  const { data: editionsData } = useQuery({
    queryKey: ['tra-editions-list'],
    queryFn: () => traAdmin.listEditions({ limit: 50, page: 1 }),
  });
  const editions: TraCertificationEdition[] = editionsData?.items ?? [];

  const [editionId, setEditionId] = useState('');
  const [certifiedAt, setCertifiedAt] = useState('');
  const [traNumber, setTraNumber] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!member) return;
    setEditionId(member.editionId ?? '');
    setCertifiedAt(member.certifiedAt ? member.certifiedAt.slice(0, 10) : '');
    setTraNumber(member.traNumber ?? '');
  }, [member]);

  const mutation = useMutation({
    mutationFn: () =>
      traAdmin.updateMemberCertification(id!, {
        editionId: editionId || null,
        certifiedAt: certifiedAt || null,
        traNumber: traNumber.trim() || null,
      }),
    onSuccess: (updated) => {
      qc.setQueryData(['tra-member', id], updated);
      qc.invalidateQueries({ queryKey: ['server-table', '/tra/members/admin'] });
      toastSuccess('Certificación actualizada.');
    },
    onError: (err) => {
      const message = getErrorMessage(err);
      setError(message);
      toastError(message);
    },
  });

  if (isLoading) return <LoadState />;
  if (!member) return <div className="p-6 text-gray-400">Miembro no encontrado.</div>;

  return (
    <ModuleLayout
      breadcrumbs={[{ label: 'SateLab', to: '/' }, 'TRA', { label: 'Miembros certificados', to: '/tra/members' }, member.fullName]}
      title={member.fullName}
      subtitle={`Certificación ${member.level}${member.institutionName ? ` · ${member.institutionName}` : ''}`}
      tabs={traNavTabs('members')}
      metrics={[]}
      actions={
        <button type="button" onClick={() => navigate('/tra/members')} className={btnSecondary}>
          ← Volver
        </button>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError('');
          mutation.mutate();
        }}
        className="max-w-xl space-y-6 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]"
      >
        <div>
          <label className={labelCls}>Edición en la que se certificó</label>
          <StyledSelect value={editionId} onChange={(e) => setEditionId(e.target.value)}>
            <option value="">Sin edición asignada</option>
            {editions.map((ed) => (
              <option key={ed.id} value={ed.id}>{ed.name}</option>
            ))}
          </StyledSelect>
        </div>

        <div>
          <label className={labelCls}>Fecha de certificación</label>
          <input
            type="date"
            className={inputCls}
            value={certifiedAt}
            onChange={(e) => setCertifiedAt(e.target.value)}
          />
        </div>

        <div>
          <label className={labelCls}>Número de certificado</label>
          <input
            type="text"
            className={inputCls}
            placeholder="Ej. 12345"
            value={traNumber}
            onChange={(e) => setTraNumber(e.target.value)}
          />
        </div>

        {error ? (
          <p role="alert" aria-live="assertive" className="text-sm font-medium text-error-500">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-5 dark:border-gray-800">
          <button type="button" onClick={() => navigate('/tra/members')} className={btnSecondary}>
            Cancelar
          </button>
          <button type="submit" disabled={mutation.isPending} className={btnSave}>
            {mutation.isPending ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </form>
    </ModuleLayout>
  );
}
